<template>
    <confirm-frame
        v-if="hasLogin"
        :order-item-obj="orderItemObj"
        :submit-order-item="submitOrderItem"
        :protocol="protocol"
        :back-url="backUrl"
        order-from="portal-park"
    ></confirm-frame>
</template>

<script>
import {regionMixin} from '../../components/mobile/mixin';
import ConfirmFrame from '../../components/order/confirmFrame.vue';
import {isLogin, skipToLoginPage, isMobile} from '../../util/helper';
import LINK from '../../util/link';
import client from '../../common/client';
import {CrsTaxpayerType, CrsComType} from '../data/order';

export default {
    mixins: [regionMixin],

    components: {
        ConfirmFrame
    },

    data() {
        return {
            hasLogin: false,
            backUrl: '',
            codeToName: {},
            codeToPinyin: {},
            queryParams: {},
            policy: null, // 价格策略
            oldParkArea: ['CP0000', 'CP0001', 'CP0002', 'CP0003', 'CP0004', 'CP0005'],
            cloudParkComRegCityMap: null,
            orderItemObj: null, // 订单确认使用参数
            submitOrderItem: null, // 提交订单使用参数
            protocol: {
                name: '《百度智能云云上园区注册协议》',
                href: 'https://cloud.baidu.com/doc/CBS/s/mkw02x0u6'
            },
            CrsTaxpayerType,
            CrsComType,
        };
    },

    created() {
        if (!isMobile()) {
            window.location.href = '/';
        }
        this.backUrl = LINK.backParkUrl;
        isLogin().then(res => {
            const hasLogin = res && res.result && res.result.hasLogin;
            if (hasLogin) {
                this.hasLogin = true;
                this.initOrderData();
            }
            else {
                skipToLoginPage();
            }
        });
    },

    methods: {
        async initOrderData() {
            // 从 url 中取出参数
            const queryParams = this.$route.query;
            const promiseArray = [
                // 获取地区静态数据
                this.getRegion(),
                // 获取活动ID
                this.queryPolicy(queryParams),
            ];
            // 获取园区新商品、个体工商户 flavor 信息
            if (!this.oldParkArea.includes(queryParams.area) || queryParams.comType === 'INDIVIDUAL') {
                promiseArray.push(this.getCloudParkFlavor(queryParams));
            }
            await Promise.all(promiseArray);

            const {nameToCode, nameToPinyin, cloudParkComRegCityMap} = this.staticRegionData;
            this.codeToName = this.setCodeToName(nameToCode);
            this.codeToPinyin = this.setCodeToPinyin(nameToCode, nameToPinyin);
            this.cloudParkComRegCityMap = cloudParkComRegCityMap;

            this.orderItemObj = this.getOrderItemObj(queryParams);
            this.submitOrderItem = this.getSubmitOrderItem(queryParams);
        },
        setCodeToName(nameToCode) {
            let result = {};
            Object.keys(nameToCode).forEach(key => {
                result[nameToCode[key]] = key;
            });
            return result;
        },
        setCodeToPinyin(nameToCode, nameToPinyin) {
            let result = {};
            Object.keys(nameToPinyin).forEach(key => {
                result[nameToCode[key]] = nameToPinyin[key];
            });
            return result;
        },
        // 获取园区、个体工商户bizType
        getBizType(comType, packageType) {
            if (comType === 'INDIVIDUAL') {
                return packageType ? 'INDIVIDUAL_BIZ_PACKAGE' : 'INDIVIDUAL_BIZ';
            }
            return packageType ? 'CLOUD_PARK_PACKAGE' : 'CLOUD_PARK_COMREG';
        },
        // 获取企服套餐
        getQifuPackageConfig(qifuPackage) {
            switch (qifuPackage) {
                case 'TMS_FD_300_30':
                    return '满300元减30元代金券(限商标自助注册，不可与新人首单优惠同享)';
                case 'FOOD_LICENSE_FD_1000_200':
                    return '满1000元减200元代金券(限食品经营许可证) ';
                case 'AIPAGE_FD_1980_198':
                    return '满1980元减198元代金券(限自助搭建官网版)';
                default:
                    return '';
            }
        },
        // 获取 serviceName
        getServiceName(comType, packageType) {
            switch (packageType) {
                case '3':
                    return '初创公司套餐';
                case '4':
                    return '公司经营套餐';
                case '5':
                    return '个体户初创套餐';
                case '6':
                    return '个体户经营套餐';
                case '7':
                    return '个体工商户注册';
                default:
                    return comType === 'INDIVIDUAL' ? '个体工商户注册' : '公司注册';
            }
        },
        // 获取服务范围
        getServiceRange(comType, packageType) {
            switch (packageType) {
                case '3':
                    return '园区代理记账、银行开户、税务报道';
                case '4':
                    return '园区代理记账、银行开户、税务报道、税控盘托管代开发票（1年内120张）';
                case '5':
                    return '代账、银行开户、税务报道';
                case '6':
                    return '代账、银行开户、税务报道、税控盘托管代开发票';
                case '7':
                    return '个体工商户注册';
                default:
                    return comType === 'INDIVIDUAL' ? '个体工商户注册' : '云上园区注册';
            }
        },
        // 查询策略
        async queryPolicy(options = {}) {
            const {area, comType, packageType} = options;
            const params = {
                areaCode: area,
                bizType: this.getBizType(comType, packageType)
            };
            const {data} = await client.getCbsPolicy(params);
            this.policy = data.pricingObjectId;
        },
        // 获取园区新商品flavor信息
        async getCloudParkFlavor(options = {}) {
            const {area, comType, taxpayerType, packageType} = options;
            const params = {
                areaCode: area,
                comType: this.CrsComType[comType],
                taxpayerType: this.CrsTaxpayerType[taxpayerType],
                bizType: this.getBizType(comType, packageType)
            };
            if (packageType) {
                params.packageType = packageType;
            }
            const {data} = await client.getCloudParkFlavor(params);
            this.flavors = data.flavors;
        },
        // 获取查询订单配置参数
        getOrderItemObj(options = {}) {
            const {area, comType, taxpayerType, packageType, qifuPackage} = options;
            let flavors = [
                {
                    name: `${this.codeToPinyin[area]}`,
                    value: `${this.CrsComType[comType]}-${this.CrsTaxpayerType[taxpayerType]}`,
                    scale: 1
                },
                {
                    name: 'subServiceType',
                    value: packageType === '3' ? 'parkPackage1' : packageType === '4' ? 'parkPackage2' : 'yuanquzhuce',
                    scale: 1
                }
            ];
            if (!this.oldParkArea.includes(area) || comType === 'INDIVIDUAL') {
                flavors = this.flavors;
            }
            let qifuPackageConfig = [];
            if (qifuPackage) {
                qifuPackageConfig = [
                    {
                        label: '赠',
                        value: this.getQifuPackageConfig(qifuPackage)
                    }
                ];
            }
            return {
                serviceType: 'CBS',
                serviceName: this.getServiceName(comType, packageType),
                count: 1,
                duration: 1,
                type: 'NEW',
                productType: 'prepay',
                subProductType: 'Project',
                region: 'global',
                timeUnit: 'DAY',
                unitText: '次',
                policy: this.policy ? this.policy : null,
                flavor: [
                    ...flavors,
                    // 拼接 crs 代金券所需要的 flavor 参数
                    ...this.getCrsCouponFlavorList(packageType, area, comType, taxpayerType)
                ],
                configDetail: [
                    {
                        label: '公司类型',
                        value: this.CrsComType[comType]
                    },
                    {
                        label: '纳税人类型',
                        value: this.CrsTaxpayerType[taxpayerType]
                    },
                    {
                        label: '地区',
                        value: this.cloudParkComRegCityMap[area]
                    },
                    {
                        label: '服务范围',
                        value: this.getServiceRange(comType, packageType)
                    },
                    ...qifuPackageConfig
                ]
            };
        },
        // 获取代金券的 flavor 参数
        getCrsCouponFlavorList(area, comType, taxpayerType) {
            return [
                {
                    name: 'productType',
                    value: 'CRS',
                    scale: 1
                },
                {
                    name: 'area',
                    value: area,
                    scale: 1
                },
                {
                    name: 'comType',
                    value: this.CrsComType[comType],
                    scale: 1
                },
                {
                    name: 'taxpayerType',
                    value: this.CrsTaxpayerType[taxpayerType],
                    scale: 1
                }
            ];
        },
        // 获取提交订单参数
        getSubmitOrderItem(options = {}) {
            const {
                solutionId,
                taxpayerType,
                area,
                packageType,
                comType,
                qifuPackage
            } = options;
            const submitOrderItem = {
                bizType: this.getBizType(comType, packageType)
            };
            if (solutionId) {
                submitOrderItem.solutionId = solutionId;
            }
            const detail = {
                bizType: this.getBizType(comType, packageType),
                area,
                taxpayerType: this.CrsTaxpayerType[taxpayerType],
                comType: this.CrsComType[comType]
            };
            if (packageType) {
                detail.packageType = packageType;
            }

            if (qifuPackage) {
                submitOrderItem.needIssueCoupons = [qifuPackage];
            }

            submitOrderItem.detail = detail;
            return submitOrderItem;
        }
    }
};
</script>